import _ from "lodash";
import styles from "./styles.module.scss";
import { useContext, useState } from "react";
import cx from "classnames";
import { useSpring, animated } from "@react-spring/web";
import useMeasure from "react-use-measure";

import WarningMessage from "src/components/warningMessage";
import { createContext } from "react";
import { useSelector } from "react-redux";

/**
 * Table of Contents component for displaying the structure of an asset.
 * It shows sections and their respective pages, allowing users to navigate through them.
 *
 * @param {Object} asset - The asset object containing details about the asset.
 * @param {Function} onClose - Callback function to close the table of contents.
 */

const TocContext = createContext<{
  activeSectionId: string | null;
  setActiveSectionId: React.Dispatch<React.SetStateAction<string | null>>;
}>({
  activeSectionId: null,
  setActiveSectionId: () => null,
});


const TableOfContents = ({ asset, onClose }) => {

  const tableOfContent = useSelector((state) => _.get(state, `asset.tableOfContents.sections`,[]));
  const contentVersion = useSelector((state) => _.get(state, `asset.tableOfContents.version`, 0));
  const assetVersion = _.get(asset, "version", 0);

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const isExpiredContents = contentVersion != assetVersion;

  return (
    <div className={styles.tableOfContents}>

      <p className={styles.title}>
        table of contents <i onClick={onClose} className="paperflite-close" />
      </p>

      {isExpiredContents && <div className={styles.warningMessage}>
        <WarningMessage
          type="WARNING"
          header="psst!"
          className={styles.message}
          message={"Heads up: The table of contents hasn't been updated with the latest assets."}
        />
      </div>}
      <div className={cx(styles.content, {
        [styles.expiredContents]: isExpiredContents
      })}>
        <TocContext.Provider value={{ activeSectionId, setActiveSectionId }}>
          <SectionBrowser data={tableOfContent} asset={asset} />
        </TocContext.Provider>
      </div>
    </div>
  );
};

const SectionBrowser = ({ data, asset, depth = 0  }) => {
  const isRoot = depth === 0;
  
  return (
    <div style={{ paddingLeft: isRoot ? 0 : "2rem" }}>
      {_.size(data) > 0 &&
        _.map(data, (section) => <SectionEntity section={section} asset={asset} depth={depth + 1}  />)}
    </div>
  );
};

const SectionEntity = ({ section, asset, depth }) => {
  const hasChildren = _.size(section.sections) > 0;
  const [showChildren, setShowChildren] = useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const { activeSectionId, setActiveSectionId } = useContext(TocContext);

  const [childrenAnimation] = useSpring(
    {
      opacity: showChildren ? 1 : 0,
      height: showChildren ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [showChildren, containerBounds],
  );

  const handleClick = () => {
    const pageNumber = section.page;
    if (pageNumber && pageNumber > 0) {
      const element = document.querySelector(`#asset_viewer_${asset.id} [data-page="${pageNumber}"]`);

      if (element) {
        var offsetTop = getOffsetTop(element);
        setActiveSectionId(section.uid);
        getScrollParent().scrollTo({
          top: offsetTop,
          behavior: "smooth"
        });
      }
    }
  }

  const getOffsetTop = (element) => {
    var offset = element.offsetTop;
    var scrollContainer = getScrollParent();

    var parent = element.offsetParent;
    while (parent != null && parent != scrollContainer) {
      offset += parent.offsetTop;
      parent = parent.offsetParent;
    }

    return offset;
  }

  const getScrollParent = () => {
    const id = `viewer_${asset.id}`;
    const parentElement = document.getElementById(id);
    if (parentElement) {
      return parentElement;
    }
    return document.body; // Fallback to body if no specific scroll container found
  }


  return (
    <div className={cx(styles.section, {
      [styles.active]: activeSectionId === section.uid,
    })}>
      <div className={styles.inner} onClick={handleClick} >
        {hasChildren && ( 
          <i
            onClick={(e) => {
              e.stopPropagation();
              hasChildren && setShowChildren(!showChildren)
            } 
            }
            className={cx("paperflite-dropdown", {
              [styles.rotate]: showChildren,
            })}
          />
        )}
        <span style={{maxWidth: depth > 1 ? "14rem" : "15rem" }} className={styles.name}> {section.name} </span>
        <span className={styles.pageNumber}> {section.page} </span>
      </div>
      <animated.div style={childrenAnimation} className={styles.children}>
        <div ref={containerRef} className={styles.inner}>
          <SectionBrowser data={section.sections} asset={asset} depth={depth} />
        </div>
      </animated.div>
    </div>
  );
};


export default TableOfContents;
